import { FC, useState, useEffect } from 'react'
import { Divider } from 'antd'
import { CenteredSpin, Pagination, Empty } from '../ui'
import { useTranslation } from 'react-i18next'
import { Filters, Controls, GeneratorCard } from './components'
import { IOwner } from './components/GeneratorCard/GeneratorCard'
import { ESortType } from './components/Controls/Controls'
import { useLocation, useNavigate } from 'react-router-dom'

import style from './style.m.less'
import useRequest, { RequestTypes } from '../hooks/useRequest'

const PAGE_SIZE = 10

interface IData {
    go_owners: IOwner[]
    total: number
    total_power_max: number
}

const Owners: FC = ({ isAuth }: any) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const {
        fetch,
        result,
        isLoading: loading,
    } = useRequest({
        link: '/api/generators/v1/go-owners',
        method: RequestTypes.post,
    })

    const data: IData = result

    const [page, setPage] = useState<number>(() => {
        if (localStorage.getItem('owners_page')) {
            return Number(localStorage.getItem('owners_page'))
        } else {
            localStorage.setItem('owners_page', '1')
            return 1
        }
    })
    const [pageSize, setPageSize] = useState<number>(() => {
        if (localStorage.getItem('owners_page_size')) {
            return Number(localStorage.getItem('owners_page_size'))
        } else {
            localStorage.setItem('owners_page_size', String(PAGE_SIZE))
            return PAGE_SIZE
        }
    })

    const [userNameFilter, setUserNameFilter] = useState(() => {
        if (localStorage.getItem('owners_user_name')) {
            return localStorage.getItem('owners_user_name')
        } else {
            localStorage.setItem('owners_user_name', '')
            return ''
        }
    })
    const [totalPowerFromFilter, setTotalPowerFromFilter] = useState(() => {
        if (localStorage.getItem('owners_total_power_from')) {
            return Number(localStorage.getItem('owners_total_power_from'))
        } else {
            localStorage.setItem('owners_total_power_from', '0')
            return 0
        }
    })
    const [totalPowerToFilter, setTotalPowerToFilter] = useState(() => {
        if (localStorage.getItem('owners_total_power_to')) {
            return Number(localStorage.getItem('owners_total_power_to'))
        } else {
            localStorage.setItem('owners_total_power_to', '0')
            return 0
        }
    })
    const [renewableEnergyTypeFilter, setRenewableEnergyTypeFilter] = useState(() => {
        if (localStorage.getItem('owners_renewable_energy_type')) {
            return JSON.parse(localStorage.getItem('owners_renewable_energy_type'))
        } else {
            localStorage.setItem('owners_renewable_energy_type', JSON.stringify([]))
            return []
        }
    })

    const [sortType, setSortType] = useState<ESortType>()
    const [descending, setDescending] = useState<boolean>()

    const fetchOwners = () => {
        fetch({
            body: {
                page,
                limit: pageSize,
                offset: (page - 1) * pageSize,
                filters: {
                    renewable_energy_type_ids: renewableEnergyTypeFilter,
                    total_power_from: totalPowerFromFilter,
                    total_power_to: totalPowerToFilter,
                    name: userNameFilter,
                },
                sort: {
                    type_id: sortType,
                    is_descending: descending,
                },
            },
        })
    }

    useEffect(() => {

        fetchOwners()

        if (location.state && location.state.fromMenu) {
            setUserNameFilter('')
            localStorage.setItem('owners_user_name', '')
            setTotalPowerFromFilter(0)
            localStorage.setItem('owners_total_power_from', '0')
            setTotalPowerToFilter(0)
            localStorage.setItem('owners_total_power_to', '0')
            setRenewableEnergyTypeFilter([])
            localStorage.setItem('owners_renewable_energy_type', JSON.stringify([]))
            setPage(1)
            localStorage.setItem('owners_page', '1')
            setPageSize(PAGE_SIZE)
            localStorage.setItem('owners_page_size', String(PAGE_SIZE))

            navigate(location.pathname, {state: {fromMenu: false}})
        }
    }, [
        page,
        pageSize,
        userNameFilter,
        renewableEnergyTypeFilter,
        totalPowerFromFilter,
        totalPowerToFilter,
        sortType,
        descending
    ]);

    return (
        <div className={style.ownersMain}>
            <div className={style.mainHeader}>{t('owners.title')}</div>
            <div className={style.mainWrapper}>
                <Controls
                    name={userNameFilter}
                    sortType={sortType}
                    desc={descending}
                    onChangeUserName={(name) => {
                        setUserNameFilter(name)
                        localStorage.setItem('owners_user_name', name)
                        setPage(1)
                        localStorage.setItem('owners_page', '1')
                        setPageSize(PAGE_SIZE)
                        localStorage.setItem('owners_page_size', String(PAGE_SIZE))
                    }}
                    onChangeSortType={setSortType}
                    onDescendingType={setDescending}
                    showDownloadData={isAuth}
                    filters={{
                        renewableEnergyType: renewableEnergyTypeFilter,
                        totalPowerFrom: totalPowerFromFilter,
                        totalPowerTo: totalPowerToFilter,
                    }}
                />
                <div className={style.mainBlock}>
                    <div className={style.cardsBlock}>
                        {loading && <CenteredSpin />}
                        {data?.go_owners ? (
                            data?.go_owners.map((owner: IOwner, index: number) => (
                                <div key={owner.uid}>
                                    <GeneratorCard owner={owner} />
                                    {index !== data.go_owners.length - 1 && (
                                        <Divider className={style.divider} />
                                    )}
                                </div>
                            ))
                        ) : (
                            <Empty
                                imageStyle={{
                                    height: 60,
                                    marginTop: 60,
                                }}
                                descriptionText={t('ownerCard.errorNothingFound')}
                            />
                        )}
                    </div>
                    <Filters
                        values={{
                            renewableEnergyType: renewableEnergyTypeFilter,
                            totalPowerFrom: totalPowerFromFilter,
                            totalPowerTo: totalPowerToFilter,
                        }}
                        onChange={({ totalPowerFrom, totalPowerTo, renewableEnergyType }) => {
                            setTotalPowerFromFilter(totalPowerFrom)
                            localStorage.setItem('owners_total_power_from', String(totalPowerFrom))
                            setTotalPowerToFilter(totalPowerTo)
                            localStorage.setItem('owners_total_power_to', String(totalPowerTo))
                            setRenewableEnergyTypeFilter(renewableEnergyType)
                            localStorage.setItem('owners_renewable_energy_type', JSON.stringify(renewableEnergyType))
                            setPage(1)
                            localStorage.setItem('owners_page', '1')
                            setPageSize(PAGE_SIZE)
                            localStorage.setItem('owners_page_size', String(PAGE_SIZE))
                        }}
                    />
                </div>
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    paginationChange={(receivedPage: number, pageSize?: number) => {
                        setPage(receivedPage)
                        localStorage.setItem('owners_page', String(receivedPage))
                        if (pageSize) {
                            setPageSize(pageSize)
                            localStorage.setItem('owners_page_size', String(pageSize))
                        }
                    }}
                    total={data?.total}
                    showSizeChanger
                />
            </div>
        </div>
    )
}

export default Owners
